/**
 * What the next chair tap does with an armed placement.
 *
 * Kept out of the editor so the one rule that is easy to get wrong can be tested
 * without a render harness: a SWAP needs two chairs to exchange. The server says
 * so plainly — AssignSeatCoreAsync refuses `seat_occupied` when the occupant
 * being placed has no chair of their own, `?swap=true` or not, because evicting
 * the incumbent to nowhere is an unseating the host never asked for.
 *
 * The two entrances arm different things and only one of them can swap. "Move to
 * another chair" arms an occupant who is already sitting somewhere, so there is a
 * chair to trade. "Seat" in the still-to-seat panel arms someone standing up, so
 * there is not — offering that host a swap confirmation sends them into a 409
 * they cannot act on.
 */
import { occupantName } from './seatingCandidates';
import type { AssignSeatRequest, SeatAssignment } from '../types/seating';

/** A chair, addressed the way the API addresses one. */
export interface PlacementSeatRef {
  tableId: string;
  seatIndex: number;
}

export interface ArmedPlacement {
  body: AssignSeatRequest;
  /** Who the next tap seats, for the wording of the question. */
  name: string;
  /** The chair they are being moved OUT of. Absent when they are not seated yet. */
  from?: PlacementSeatRef;
}

export type PlacementOutcome =
  /** They tapped the chair the occupant is already in: a change of mind, not a move. */
  | { kind: 'cancel' }
  | { kind: 'seat' }
  /** Ask first — a swap moves someone the host did not name. */
  | { kind: 'swap'; prompt: string }
  /** Nothing to send: the API would refuse, and the refusal has no recovery. */
  | { kind: 'refuse'; reason: string };

export function placementOutcome(
  placement: ArmedPlacement,
  target: PlacementSeatRef,
  occupant: SeatAssignment | null,
): PlacementOutcome {
  const { from } = placement;
  if (from && from.tableId === target.tableId && from.seatIndex === target.seatIndex) {
    return { kind: 'cancel' };
  }
  if (!occupant) return { kind: 'seat' };

  const other = occupantName(occupant);
  if (!from) {
    return {
      kind: 'refuse',
      reason: `${other} is already in that chair, and ${placement.name} has no chair to trade for it.`
        + ` Pick an empty chair, or take ${other} out of that one first.`,
    };
  }
  return { kind: 'swap', prompt: `${other} is in that chair. Swap them with ${placement.name}?` };
}
